import { translate } from '@jsverse/transloco';

import {
  BreakdownOptions,
  WidgetChartOptions,
} from 'app/modules/widget/components/widget-chart/widget-chart.component';
import { Comment, Dashboard, Widget } from 'app/providers/bridge-api/dashboard.service';
import { FieldService } from 'app/providers/storage-api/field.service';
import { Report } from 'app/providers/storage-api/report.service';
import { Utils } from 'app/utils/utils';
import {
  AbstractChartReport,
  CommentDataChange,
} from '../chart-report-abstract/chart-report-abstract';
import { ChartDataService } from '../../chart-data.service';
import { HighchartsOptions } from '../../chart-display.service';

export class BreakdownChartReport extends AbstractChartReport {
  public reportName = 'documentNumericMetadata';
  public reportHandler = ChartDataService.forBreakdown;

  public getFields(): string[] {
    return [];
  }

  public getGraphTypes(): string[] {
    return ['column', 'bar'];
  }

  canColorByPoint(): boolean {
    return true;
  }

  canDisplayChartValues(): boolean {
    return true;
  }

  isColorByPointDisabled(): boolean {
    return (
      this.options.breakdownOptions.scoring === 'box' ||
      this.options.breakdownOptions.scoring === 'bucket'
    );
  }

  isDisplayChartValuesDisabled(): boolean {
    return (
      this.options.breakdownOptions.scoring === 'box' ||
      this.options.breakdownOptions.scoring === 'bucket'
    );
  }

  colorByPointDisabledTooltip(): string {
    return translate('chart-report.top-box-color-by-point');
  }

  displayChartValuesDisabledTooltip(): string {
    return translate('chart-report.top-box-display-values');
  }

  public applyComparisonToSeries(_comparison: any, _series: any, _seriesIdx: any): void {
    return;
  }

  protected getReportSpecificCommentDataChange(
    comment: Comment,
    widget: Widget<WidgetChartOptions>,
    _dashboard: Dashboard,
    currentData: any,
    dataProp: string
  ): CommentDataChange {
    const changeReasons = [];
    if (
      comment.payload.breakdownOptions.numericMetadataFieldId !==
      widget.payload.breakdownOptions.numericMetadataFieldId
    ) {
      changeReasons.push('METADATA_FIELD_CHANGED');
    }

    if (comment.payload.breakdownOptions.scoring !== widget.payload.breakdownOptions.scoring) {
      changeReasons.push('SCORING_STRATEGY_CHANGED');
    }

    if (
      /* eslint-disable eqeqeq */
      comment.payload.breakdownOptions.bottomBoxSize !=
        widget.payload.breakdownOptions.bottomBoxSize ||
      comment.payload.breakdownOptions.topBoxSize != widget.payload.breakdownOptions.topBoxSize
      /* eslint-enable eqeqeq */
    ) {
      changeReasons.push('BOX_SIZE_CHANGED');
    }

    if (
      /* eslint-disable eqeqeq */
      comment.payload.breakdownOptions.min != widget.payload.breakdownOptions.min ||
      comment.payload.breakdownOptions.max != widget.payload.breakdownOptions.max
      /* eslint-enable eqeqeq */
    ) {
      changeReasons.push('SCORING_SCALE_CHANGED');
    }

    if (
      comment.payload.breakdownOptions.scoring === 'bucket' &&
      widget.payload.breakdownOptions.scoring === 'bucket' &&
      !Utils.deepEquals(
        comment.payload.breakdownOptions.fieldBuckets,
        widget.payload.breakdownOptions.fieldBuckets
      )
    ) {
      changeReasons.push('BUCKET_DEFINITION_CHANGED');
    }

    if (changeReasons.length) {
      return {
        [dataProp]: currentData?.[dataProp],
        reasons: changeReasons,
      };
    }
  }

  public getParams() {
    const params = super.getParams();

    params.breakdownField = this.options.sectionPropertySelected
      ? this.options.breakdownOptions.fieldId.replace('sectionProperties.', 'flatSections.')
      : this.options.breakdownOptions.fieldId;
    params.breakdownSize = this.options.limit;

    const bucketOptions = [
      this.options.breakdownOptions,
      this.options.additionalBreakdownOptions,
    ].find(
      (opts) =>
        opts?.scoring === 'bucket' &&
        Array.isArray(opts.fieldBuckets) &&
        opts.fieldBuckets.length > 0
    ) as BreakdownOptions | undefined;

    if (bucketOptions?.fieldBuckets?.length) {
      params.fieldBuckets = JSON.stringify(
        bucketOptions.fieldBuckets.map((bucket) => ({
          bucketName: bucket.bucketName,
          minimumValue: bucket.minimumValue,
          maximumValue: bucket.maximumValue,
          includeMinimum: bucket.includeMinimum,
          includeMaximum: bucket.includeMaximum,
        }))
      );
    }

    return params;
  }

  public async executeReport(
    format: 'json' | 'csv',
    passedQuery: string
  ): Promise<string | Report<{ [key: string]: any }>> {
    const params = await this.getParams();
    const body = this.getMapData();
    const buildFieldBucketsParam = (opts?: Partial<BreakdownOptions>): string | undefined => {
      if (!Array.isArray(opts?.fieldBuckets) || opts.fieldBuckets.length === 0) {
        return undefined;
      }
      return JSON.stringify(
        opts.fieldBuckets.map((bucket) => ({
          bucketName: bucket.bucketName,
          minimumValue: bucket.minimumValue,
          maximumValue: bucket.maximumValue,
          includeMinimum: bucket.includeMinimum,
          includeMaximum: bucket.includeMaximum,
        }))
      );
    };

    const isTwoAxisBucketScoring =
      format === 'json' &&
      this.options.hasSecondaryAxis === true &&
      this.options.breakdownOptions?.scoring === 'bucket' &&
      this.options.additionalBreakdownOptions?.scoring === 'bucket';

    if (isTwoAxisBucketScoring) {
      const fieldBucketsPrimary = buildFieldBucketsParam(this.options.breakdownOptions);
      const fieldBucketsSecondary = buildFieldBucketsParam(this.options.additionalBreakdownOptions);

      const paramsPrimary = { ...params };
      if (fieldBucketsPrimary) {
        paramsPrimary.fieldBuckets = fieldBucketsPrimary;
      } else {
        delete paramsPrimary.fieldBuckets;
      }

      const paramsSecondary = { ...params };
      if (fieldBucketsSecondary) {
        paramsSecondary.fieldBuckets = fieldBucketsSecondary;
      } else {
        delete paramsSecondary.fieldBuckets;
      }

      const [responsePrimary, responseSecondary] = await Promise.all([
        this.reportService.postReport(
          this.options.widgetDataSource.collectionName,
          this.options.widgetDataSource.groupId,
          this.reportName,
          paramsPrimary,
          [body[0]],
          format,
          passedQuery,
          undefined,
          undefined,
          this.options.projectId
        ) as Promise<Report<{ [key: string]: any }>>,
        this.reportService.postReport(
          this.options.widgetDataSource.collectionName,
          this.options.widgetDataSource.groupId,
          this.reportName,
          paramsSecondary,
          [body[1]],
          format,
          passedQuery,
          undefined,
          undefined,
          this.options.projectId
        ) as Promise<Report<{ [key: string]: any }>>,
      ]);

      const mergedResponse: Report<{ [key: string]: any }> = {
        ...(responsePrimary as Report<{ [key: string]: any }>),
        items: {},
      };

      const primaryItems: Record<string, any> = (responsePrimary as any)?.items ?? {};
      const secondaryItems: Record<string, any> = (responseSecondary as any)?.items ?? {};
      const categories = new Set<string>([
        ...Object.keys(primaryItems),
        ...Object.keys(secondaryItems),
      ]);

      for (const category of categories) {
        const primaryItem = primaryItems[category] ?? {};
        const secondaryItem = secondaryItems[category] ?? {};
        const primaryMetric = Array.isArray(primaryItem.metadata) ? primaryItem.metadata[0] : {};
        const secondaryMetric = Array.isArray(secondaryItem.metadata)
          ? secondaryItem.metadata[0]
          : {};
        mergedResponse.items[category] = {
          ...primaryItem,
          metadata: [primaryMetric ?? {}, secondaryMetric ?? {}],
        };
      }

      this.lastDocumentCount = mergedResponse.totalHits;
      return await this.processResponse(mergedResponse, passedQuery);
    }

    const response = await this.reportService.postReport(
      this.options.widgetDataSource.collectionName,
      this.options.widgetDataSource.groupId,
      this.reportName,
      params,
      body,
      format,
      passedQuery,
      undefined,
      undefined,
      this.options.projectId
    );

    if (format === 'json') {
      this.lastDocumentCount = (<Report>response).totalHits;

      return await this.processResponse(response, passedQuery);
    }

    return response;
  }

  public getMapData(includeLabels = false): Array<{ [key: string]: string }> {
    const isNumericMetadataCustomField = (fieldId: string): boolean => {
      if (!fieldId) {
        return false;
      }
      return FieldService.isCustomField(fieldId) || fieldId.toLowerCase().startsWith('nd_');
    };

    const fillMapBody = (
      body: { [key: string]: string },
      breakdownOpts: Partial<BreakdownOptions>
    ) => {
      if (breakdownOpts) {
        if (isNumericMetadataCustomField(breakdownOpts.numericMetadataFieldId)) {
          body['customField'] = breakdownOpts.numericMetadataFieldId;
          delete body['standardField'];
        } else {
          body['standardField'] = breakdownOpts.numericMetadataFieldId;
          delete body['customField'];
        }

        switch (breakdownOpts.scoring) {
          case 'avg':
            body['scoringStrategy'] = 'AVERAGE';
            break;
          case 'sum':
            body['scoringStrategy'] = 'SUM';
            break;
          case 'bucket':
            body['scoringStrategy'] = 'BUCKET';
            break;
          case 'box':
            body['scoringStrategy'] = 'BOX';
            body['scoreRange'] = `${breakdownOpts.min}:${breakdownOpts.max}`;
            body['boxTopSize'] = breakdownOpts.topBoxSize;
            body['boxBottomSize'] = breakdownOpts.bottomBoxSize;
            break;
          case 'nps':
            body['scoringStrategy'] = 'NPS';
            if (breakdownOpts.max) {
              body['scoreRange'] = `${breakdownOpts.min}:${breakdownOpts.max}`;
            }
            break;
        }
      }
    };
    const returnValue = [];

    for (const key of ['breakdownOptions', 'additionalBreakdownOptions']) {
      const opts = this.options[key] as BreakdownOptions;
      if (!opts) {
        continue;
      }

      const body: { [key: string]: string } = {
        scoringStrategy: '',
        label: '',
      };
      if (opts.numericMetadataFieldId === 'docs') {
        body.customField = 'Documents';
        body.scoringStrategy = 'COUNT';
        if (includeLabels) {
          body.label = 'Documents';
        }
      } else {
        body.label = FieldService.getFieldLongName(opts.numericMetadataFieldId);
        fillMapBody(body, opts);
      }
      returnValue.push(body);
    }

    return returnValue;
  }

  public getHighchartsOptions(
    dashboard: Dashboard,
    widget: Widget<WidgetChartOptions>,
    response: any,
    options: HighchartsOptions
  ): HighchartsOptions {
    const result = super.getHighchartsOptions(dashboard, widget, response, options);

    if (
      this.canDisplayChartValues() &&
      !this.isDisplayChartValuesDisabled() &&
      widget.payload.displayChartValues
    ) {
      const globalDataLabels = result.plotOptions.series.dataLabels;
      result.series.forEach((s) => {
        const isSecondary = s.yAxis === 1;
        s.dataLabels = {
          ...globalDataLabels,
          y: isSecondary ? 7 : -7,
          verticalAlign: isSecondary ? 'middle' : 'bottom',
          color: s.color || undefined,
        };
      });
      result.plotOptions.series.dataLabels = { allowOverlap: true };
    }

    if (this.canColorByPoint() && !this.isColorByPointDisabled() && widget.payload.colorByPoint) {
      const nonBoxScoringSeries = result.series.filter((s) => !s.color);
      if (nonBoxScoringSeries.length !== result.series.length) {
        nonBoxScoringSeries.forEach((serie) => (serie.colorByPoint = true));
        delete result.plotOptions.series.colorByPoint; // we need to remove this since it is coming from abstract
      } else {
        result.plotOptions.series.colorByPoint = true;
      }
    }

    if (!Array.isArray(options.yAxis)) {
      this.setChartAxisValues(
        widget.payload.chartMinimum,
        widget.payload.chartMaximum,
        widget.payload.chartInterval,
        options.yAxis
      );
    }

    if (Array.isArray(options.yAxis)) {
      const keys = new Map<number, Array<string>>([
        [0, ['chartMinimum', 'chartMaximum', 'chartInterval']],
        [1, ['chartSecondaryMinimum', 'chartSecondaryMaximum', 'chartSecondaryInterval']],
      ]);
      options.yAxis.forEach((yAxis, index) => {
        // Delete b/c of abstract will modify these and not all axes needs these
        delete yAxis.min;
        delete yAxis.max;
        delete yAxis.tickInterval;
        this.setChartAxisValues(
          widget.payload[keys.get(index)[0]],
          widget.payload[keys.get(index)[1]],
          widget.payload[keys.get(index)[2]],
          yAxis
        );
      });
    }

    result.tooltip = {
      headerFormat: `${translate('widgets.charting.tooltip.name-part', {
        name: "'{point.key'}",
      })}<br/>`,
      valueDecimals: 2,
      pointFormatter: function () {
        return `${translate('widgets.charting.tooltip.document-count-part', {
          count: Utils.addThousandSeparator(Utils.round(this.options.documentCount)),
        })}`;
      },
    };

    return result;
  }
}
